/**
 * A single-pick question, from the prototype's question card (lines
 * ~135-142). Numbered options; the picked one tints, the rest fade once
 * answered, and every option stops responding to a click.
 */

import { useState } from 'react';
import { answerCard, ApiError } from '../../api';
import type { CardProps } from './types';

export function QuestionCard({ chatId, message, onChange }: CardProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (message.body.kind !== 'question') return null;
  const { question, options, answer } = message.body;

  const pick = (opt: string) => {
    if (answer || busy) return;
    setBusy(true);
    setError(null);
    answerCard(chatId, message.id, { answer: opt })
      .then(onChange)
      .catch((err: unknown) =>
        setError(err instanceof ApiError ? err.message : 'That did not go through. Try again.')
      )
      .finally(() => setBusy(false));
  };

  return (
    <div
      style={{
        background: 'var(--surface-card)',
        borderRadius: 'var(--radius-app-card)',
        padding: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 10
      }}
    >
      <span style={{ fontSize: 14.5, fontWeight: 600, color: 'var(--text-strong)' }}>{question}</span>
      {options.map((o, i) => {
        const selected = answer === o;
        const dim = !!answer && !selected;
        return (
          <button
            key={o}
            type="button"
            className={answer ? 'bare' : 'bare hov-border'}
            disabled={!!answer || busy}
            onClick={() => pick(o)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              minHeight: 40,
              padding: '0 12px',
              borderRadius: 'var(--radius-sm)',
              border: `1px solid ${selected ? 'var(--primary)' : 'var(--border-strong)'}`,
              background: selected ? 'var(--primary-soft)' : 'transparent',
              fontSize: 13.5,
              color: dim ? 'var(--text-faint)' : 'var(--text-strong)',
              cursor: answer ? 'default' : 'pointer'
            }}
          >
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, color: 'var(--text-faint)' }}>{i + 1}</span>
            {o}
          </button>
        );
      })}
      {error ? <span style={{ fontSize: 12.5, color: 'var(--danger-600)' }}>{error}</span> : null}
    </div>
  );
}
